import styled from '@emotion/styled';
import type { Site } from '@hotjar/site';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { capitalize } from '@hotjar/insights-common/lib/string';
import { epochDate } from '@hotjar/webapp/lib/time';
import { Icon, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

const ArrowDownIcon = styled(Icon)`
  margin: 0 ${tokens.spacer.x1} 0 0;
`;

const DowngradeMessageContainer = styled.div`
  ${tokens.text('caption')};
  color: ${tokens.color.text_light};
  display: flex;
`;

interface Props {
  site: Site;
  effectiveAllowance: number;
  futureAllowance: number;
  effectiveFeatureGroup: FeatureGroup;
  futureFeatureGroup: FeatureGroup;
}

export function MultiSkuDowngrade({
  site,
  effectiveAllowance,
  futureAllowance,
  effectiveFeatureGroup,
  futureFeatureGroup,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const planChangeDate = epochDate(site.planChangeDate ?? undefined);

  return (
    <DowngradeMessageContainer>
      <ArrowDownIcon size="l" color="text_error" icon="arrow_down" />
      <span>
        {effectiveFeatureGroup === futureFeatureGroup && (
          <span data-testid="fair-downgrade-same-featuregroup">
            {t('billing:site-box.downgrade-from', {
              downgradeInitial: abbrNumber(effectiveAllowance),
            })}{' '}
            &rarr;{' '}
            {t('billing:site-box.downgrade-to', {
              downgradeFinal: abbrNumber(futureAllowance),
              sessionOrPageView: t('billing:site-box.daily-sessions'),
              planChangeDate,
            })}
          </span>
        )}

        {effectiveFeatureGroup !== futureFeatureGroup && (
          <span data-testid="fair-downgrade-different-featuregroup">
            {t('billing:site-box.downgrade-from', {
              downgradeInitial: capitalize(effectiveFeatureGroup),
            })}{' '}
            &rarr;{' '}
            {t('billing:site-box.downgrade-to', {
              downgradeFinal: capitalize(futureFeatureGroup),
              planChangeDate,
            })}
          </span>
        )}
      </span>
    </DowngradeMessageContainer>
  );
}
